import { defineType } from "sanity";
import {
  multilingualArrayBlockContent,
  multilingualArrayString
} from "../base";
import { multilingualStringPreview } from "../base/multilingualArrayString";
import imageWithAlt from "./imageWithAlt";

// This object used to keep the legacy multilingual types — the
// internationalized-array plugin was not injecting the language context this
// deep in the tree, hence the rollbacks in 5601dcf, 12ed136 and cdc3f7d. The
// dataset is realigned by `migrate-suite-configuration-to-multilingual-array`:
// run it on a dataset before deploying this schema to it, or the editor renders
// these fields empty.

export default defineType({
  name: "suiteConfiguration",
  title: "Suite Configuration",
  type: "object",
  preview: {
    select: {
      title: "code",
      name: "name",
      image: "plan"
    },
    prepare(value) {
      return Object.assign({}, value, {
        title: value.title,
        subtitle: multilingualStringPreview(value.name),
        media: value.image
      });
    }
  },
  fields: [
    {
      name: "code",
      title: "Code",
      description: "e.g. O2",
      type: "string",
      validation: (Rule) => Rule.required()
    },
    {
      name: "name",
      title: "Name",
      description: "e.g. One bedroom, Accessible",
      type: multilingualArrayString.name
    },
    {
      name: "shortSizeDescriptionInSquareFeet",
      title: "Short size description (sqft)",
      description: "e.g. 'Up to 827 ft² including veranda'",
      type: multilingualArrayString.name
    },
    {
      name: "shortSizeDescriptionInSquareMetres",
      title: "Short size description (sqm)",
      description: "e.g. 'Up to 77 m² including veranda",
      type: multilingualArrayString.name
    },
    {
      name: "sizeInSquareFeet",
      title: "Size (sqft)",
      description: "e.g. 827",
      type: "number"
    },
    {
      name: "sizeInSquareMetres",
      title: "Size (sqm)",
      description: "e.g. 77",
      type: "number"
    },
    {
      name: "verandaSizeInSquareFeet",
      title: "Veranda size (sqft)",
      description: "e.g. 'Up to 77 ft²",
      type: multilingualArrayString.name
    },
    {
      name: "verandaSizeInSquareMetres",
      title: "Veranda size (sqm)",
      description: "e.g. 'Up to 827 m²",
      type: multilingualArrayString.name
    },
    {
      name: "verandaCustomDescriptionInSquareFeet",
      title: "Veranda custom description (sqft)",
      description:
        "e.g. '430 sq ft balcony with private pool', 'Large balcony up to 140 sq ft'",
      type: multilingualArrayString.name
    },
    {
      name: "verandaCustomDescriptionInSquareMetres",
      title: "Veranda custom description (sqm)",
      description:
        "e.g. '40 sq m balcony with private pool', 'Large balcony up to 13 sq m'",
      type: multilingualArrayString.name
    },
    {
      name: "suiteHighlight",
      title: "Suite Highlight",
      description:
        "e.g. 'Corner suite with stunning 270 views', 'Panoramic window'",
      type: multilingualArrayString.name
    },
    {
      name: "extraFeatures",
      title: "Extra features",
      description:
        "e.g. 'Large bathroom and walk-in wardrobe', 'Expansive dining room and living area'",
      type: multilingualArrayString.name
    },
    {
      name: "configurationInformation",
      title: "Configuration Information",
      description:
        "e.g. Please note that the 3rd guest will sleep on a comfortable sofa bed in the reception area of the suite.",
      type: multilingualArrayBlockContent.name,
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "plan",
      title: "Plan",
      type: imageWithAlt.name
    },
    {
      name: "roomNumberFilter",
      title: "Room Number Filter",
      type: "array",
      of: [
        {
          type: "number"
        }
      ]
    }
  ]
});
